/**
 * ADF XML Fix Generator
 * 
 * Rule-based auto-fix utility that generates corrected XML based on validation errors.
 * No AI required - uses database-driven rules and heuristics.
 */

import type { AdfElementNode } from '@/hooks/useAdfStandard';

/**
 * Types of fixes that can be applied
 */
export type FixType = 
  | 'case-correction'      // Used → used
  | 'attribute-value'      // invalid attr value → valid one
  | 'remove-attribute'     // non-standard attr removed
  | 'add-element'          // missing required element added
  | 'replace-element'      // typo/extension → correct element
  | 'comment-out';         // non-standard → commented

/**
 * Represents a single fix applied to the XML
 */
export interface AppliedFix {
  path: string;
  fixType: FixType;
  original: string;
  fixed: string;
  description: string;
}

/**
 * Result of the fix generation process
 */
export interface FixGeneratorResult {
  /** The corrected XML string */
  fixedXml: string;
  /** List of fixes that were applied */
  appliedFixes: AppliedFix[];
  /** Errors that could not be auto-fixed */
  unfixableErrors: Array<{ path: string; reason: string }>;
  /** Whether all errors were fixed */
  allFixed: boolean;
}

/**
 * Validation error from AdfXmlValidator
 */
export interface ValidationError {
  path: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  category?: 'unknown' | 'extension' | 'missing' | 'invalid-attr' | 'suggestion' | 'parse';
  suggestion?: string;
}

/**
 * Known attribute value mappings for case corrections
 * Updated for ADF v2.0 - includes sms_opt_out
 */
const ATTRIBUTE_CASE_MAPPINGS: Record<string, string[]> = {
  status: ['new', 'used', 'certified'],
  interest: ['buy', 'lease', 'sell', 'trade-in', 'test-drive'],
  type: ['individual', 'business', 'voice', 'phone', 'cellphone', 'fax', 'pager', 'sms_opt_out'],
  part: ['first', 'middle', 'last', 'suffix', 'full'],
  method: ['cash', 'finance', 'lease'],
  currency: ['USD', 'CAD', 'EUR', 'GBP'],
};

/**
 * Attribute value replacements for legacy/extension values
 * Maps non-standard values to their standard equivalents
 */
const ATTRIBUTE_VALUE_REPLACEMENTS: Record<string, Record<string, string>> = {
  type: {
    'mobilephone': 'cellphone',
    'mobile': 'cellphone',
  },
};

/**
 * Element replacement mappings (extensions → standard)
 */
const ELEMENT_REPLACEMENTS: Record<string, { tag: string; attrs?: string }> = {
  'tradein': { tag: 'vehicle', attrs: 'interest="trade-in"' },
  'trade-in': { tag: 'vehicle', attrs: 'interest="trade-in"' },
  'dealer': { tag: 'vendor' },
  'dealership': { tag: 'vendor' },
  'source': { tag: 'provider' },
  'leadprovider': { tag: 'provider' },
  'leadsource': { tag: 'provider' },
  'automobile': { tag: 'vehicle' },
  'car': { tag: 'vehicle' },
  'buyer': { tag: 'customer' },
};

/**
 * Attempt to correct attribute value - handles case correction and legacy value replacement
 */
function correctAttributeValue(attrName: string, value: string): string | null {
  const attrNameLower = attrName.toLowerCase();
  const lowerValue = value.toLowerCase();
  
  // First check for direct replacements (legacy/extension values)
  const replacements = ATTRIBUTE_VALUE_REPLACEMENTS[attrNameLower];
  if (replacements && replacements[lowerValue]) {
    return replacements[lowerValue];
  }
  
  // Then check for case corrections
  const validValues = ATTRIBUTE_CASE_MAPPINGS[attrNameLower];
  if (!validValues) return null;
  const match = validValues.find(v => v.toLowerCase() === lowerValue);
  return match || null;
}

/**
 * Generate a template for a missing required element
 */
function generateMissingElementTemplate(
  tagName: string,
  elementTree: AdfElementNode[]
): string {
  // Find the element spec in the tree
  const findSpec = (nodes: AdfElementNode[]): AdfElementNode | null => {
    for (const node of nodes) {
      if (node.tag_name === tagName) return node;
      if (node.children) {
        const found = findSpec(node.children);
        if (found) return found;
      }
    }
    return null;
  };
  
  const spec = findSpec(elementTree);
  
  if (spec) {
    // Check if we have example_value to use
    if (spec.example_value) {
      // If example_value contains full XML, use it
      if (spec.example_value.startsWith('<')) {
        return spec.example_value;
      }
      return `<${tagName}>${spec.example_value}</${tagName}>`;
    }
    
    // Check for required children
    const requiredChildren = spec.children?.filter(c => c.is_required) || [];
    if (requiredChildren.length > 0) {
      const childTemplates = requiredChildren
        .map(child => generateMissingElementTemplate(child.tag_name, elementTree))
        .join('\n      ');
      return `<${tagName}>\n      ${childTemplates}\n    </${tagName}>`;
    }
    
    return `<${tagName}><!-- Required --></${tagName}>`;
  }
  
  return `<${tagName}><!-- Required --></${tagName}>`;
}

/**
 * Parse path to extract element and attribute info
 * e.g., "/adf/prospect/vehicle/@status" → { element: "vehicle", attr: "status" }
 */
function parsePath(path: string): { elements: string[]; attr?: string } {
  const parts = path.split('/').filter(Boolean);
  const lastPart = parts[parts.length - 1];
  
  if (lastPart?.startsWith('@')) {
    return {
      elements: parts.slice(0, -1),
      attr: lastPart.substring(1),
    };
  }
  
  return { elements: parts };
}

/**
 * Apply fixes to XML string based on validation errors
 */
export function generateFixedXml(
  originalXml: string,
  errors: ValidationError[],
  elementTree: AdfElementNode[]
): FixGeneratorResult {
  const appliedFixes: AppliedFix[] = [];
  const unfixableErrors: Array<{ path: string; reason: string }> = [];
  
  // Check for parse errors first - these are unfixable
  const parseErrors = errors.filter(e => e.category === 'parse');
  if (parseErrors.length > 0) {
    return {
      fixedXml: originalXml,
      appliedFixes: [],
      unfixableErrors: parseErrors.map(e => ({
        path: e.path,
        reason: 'XML is malformed and cannot be parsed. Please fix syntax errors manually.',
      })),
      allFixed: false,
    };
  }
  
  let fixedXml = originalXml;
  
  // Group and process errors by type
  for (const error of errors) {
    const { path, message, category, suggestion } = error;
    const parsed = parsePath(path);
    
    // 1. Handle invalid attribute values (case corrections)
    if (category === 'invalid-attr' && parsed.attr) {
      const attrMatch = message.match(/Invalid attribute value "([^"]+)"/);
      if (attrMatch) {
        const invalidValue = attrMatch[1];
        const correctedValue = correctAttributeValue(parsed.attr, invalidValue);
        
        if (correctedValue) {
          // Create regex to find and replace the attribute
          const regex = new RegExp(
            `(${parsed.attr}\\s*=\\s*)["']${escapeRegex(invalidValue)}["']`,
            'gi'
          );
          const newXml = fixedXml.replace(regex, `$1"${correctedValue}"`);
          
          if (newXml !== fixedXml) {
            fixedXml = newXml;
            appliedFixes.push({
              path,
              fixType: 'case-correction',
              original: `${parsed.attr}="${invalidValue}"`,
              fixed: `${parsed.attr}="${correctedValue}"`,
              description: `Corrected case: "${invalidValue}" → "${correctedValue}"`,
            });
          }
        } else {
          // Can't auto-fix this attribute value
          unfixableErrors.push({
            path,
            reason: `No valid value found for "${invalidValue}". Valid options: see ADF spec.`,
          });
        }
      }
    }
    
    // 2. Handle element suggestions (typos)
    else if (category === 'suggestion' && suggestion) {
      const tagMatch = message.match(/Unknown element <([^>]+)>/);
      if (tagMatch) {
        const wrongTag = tagMatch[1];
        const correctTag = suggestion;
        
        // Replace opening and closing tags
        const openRegex = new RegExp(`<${escapeRegex(wrongTag)}(\\s|>|/)`, 'gi');
        const closeRegex = new RegExp(`</${escapeRegex(wrongTag)}>`, 'gi');
        
        const newXml = fixedXml
          .replace(openRegex, `<${correctTag}$1`)
          .replace(closeRegex, `</${correctTag}>`);
        
        if (newXml !== fixedXml) {
          fixedXml = newXml;
          appliedFixes.push({
            path,
            fixType: 'replace-element',
            original: `<${wrongTag}>`,
            fixed: `<${correctTag}>`,
            description: `Fixed typo: <${wrongTag}> → <${correctTag}>`,
          });
        }
      }
    }
    
    // 3. Handle known extensions with suggestions
    else if (category === 'extension' && suggestion) {
      const tagName = parsed.elements[parsed.elements.length - 1];
      const replacement = ELEMENT_REPLACEMENTS[tagName?.toLowerCase()];
      
      if (replacement) {
        const openRegex = new RegExp(`<${escapeRegex(tagName)}(\\s*)>`, 'gi');
        const closeRegex = new RegExp(`</${escapeRegex(tagName)}>`, 'gi');
        
        const openReplacement = replacement.attrs 
          ? `<${replacement.tag} ${replacement.attrs}>`
          : `<${replacement.tag}>`;
        
        const newXml = fixedXml
          .replace(openRegex, openReplacement)
          .replace(closeRegex, `</${replacement.tag}>`);
        
        if (newXml !== fixedXml) {
          fixedXml = newXml;
          appliedFixes.push({
            path,
            fixType: 'replace-element',
            original: `<${tagName}>`,
            fixed: openReplacement,
            description: `Replaced non-standard element with ADF equivalent`,
          });
        }
      } else {
        unfixableErrors.push({
          path,
          reason: `Extension element cannot be auto-replaced. Consider using <comments> for this data.`,
        });
      }
    }
    
    // 4. Handle missing required elements
    else if (category === 'missing') {
      const missingTag = parsed.elements[parsed.elements.length - 1];
      if (missingTag) {
        const template = generateMissingElementTemplate(missingTag, elementTree);
        
        // Find the parent element to insert into
        const parentPath = parsed.elements.slice(0, -1);
        const parentTag = parentPath[parentPath.length - 1];
        
        if (parentTag) {
          // Find the closing tag of the parent and insert before it
          const closeTagRegex = new RegExp(`(\\s*)</${escapeRegex(parentTag)}>`, 'i');
          const match = fixedXml.match(closeTagRegex);
          
          if (match) {
            const indent = '    '; // Standard indentation
            const insertion = `${indent}${template}\n${match[1]}`;
            const newXml = fixedXml.replace(closeTagRegex, `\n${insertion}</${parentTag}>`);
            
            if (newXml !== fixedXml) {
              fixedXml = newXml;
              appliedFixes.push({
                path,
                fixType: 'add-element',
                original: '(missing)',
                fixed: template.substring(0, 50) + (template.length > 50 ? '...' : ''),
                description: `Added missing required element <${missingTag}>`,
              });
            }
          }
        }
      }
    }
    
    // 5. Unknown elements without suggestions
    else if (category === 'unknown' && error.type === 'warning') {
      // Can't auto-fix unknown elements
      unfixableErrors.push({
        path,
        reason: `Unknown element. Review if this should be removed or replaced with a standard element.`,
      });
    }
  }
  
  // Determine if all errors were fixed
  const errorCount = errors.filter(e => e.type === 'error').length;
  const fixedErrorCount = appliedFixes.filter(f => 
    ['case-correction', 'attribute-value', 'add-element', 'replace-element'].includes(f.fixType)
  ).length;
  
  return {
    fixedXml,
    appliedFixes,
    unfixableErrors,
    allFixed: unfixableErrors.length === 0 && fixedErrorCount >= errorCount,
  };
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Count fixable errors from a list of validation errors
 */
export function countFixableErrors(errors: ValidationError[]): number {
  return errors.filter(e => {
    // Parse errors are not fixable
    if (e.category === 'parse') return false;
    
    // Invalid attributes with case issues or legacy values are usually fixable
    if (e.category === 'invalid-attr') {
      const match = e.message.match(/Invalid attribute value "([^"]+)"/);
      if (match) {
        const value = match[1];
        const lowerValue = value.toLowerCase();
        
        // Check if it's a replaceable legacy value
        for (const replacements of Object.values(ATTRIBUTE_VALUE_REPLACEMENTS)) {
          if (lowerValue in replacements) {
            return true;
          }
        }
        
        // Check if it's a case issue
        for (const validValues of Object.values(ATTRIBUTE_CASE_MAPPINGS)) {
          if (validValues.some(v => v.toLowerCase() === lowerValue)) {
            return true;
          }
        }
      }
      return false;
    }
    
    // Elements with suggestions are fixable
    if (e.category === 'suggestion' && e.suggestion) return true;
    
    // Extensions with known replacements are fixable
    if (e.category === 'extension' && e.suggestion) {
      const path = e.path.split('/').filter(Boolean);
      const tag = path[path.length - 1]?.toLowerCase();
      return tag && tag in ELEMENT_REPLACEMENTS;
    }
    
    // Missing elements are fixable (we can add templates)
    if (e.category === 'missing') return true;
    
    return false;
  }).length;
}
